import axios from '@/plugins/axios'

const state = {
    token: localStorage.getItem('user_access_token') || null,
    userInfo: null,
    roles: [],
    isLoggedIn: false
}

// getters
const getters = {

    userId (state) {
        return state.userInfo.id
    },

    userInfo (state) {
        return state.userInfo
    },

    isLoggedIn (state) {
        return state.isLoggedIn
    },

    isAdmin (state) {
        return state.roles.indexOf(1) > -1;
    },

    hasRole (state, id) {
        return state.roles.indexOf(id) > -1;
    },
}

// actions
const actions = {
    /**
     * log user in
     * @param context
     * @param credentials
     * @returns {Promise<any>}
     */
    login({ commit }, credentials) {
        return new Promise((resolve, reject) => {
            axios.post('/login', {
                username: credentials.username,
                password: credentials.password,
            })
                .then(response => {
                    let token = response.data.access_token;
                    commit('setToken', token);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * log user out
     * @param context
     * @returns {Promise<any>}
     */
    logout({ state, commit }) {
        commit('addToLoadingQueue', 'user_logout', { root: true });

        return new Promise((resolve, reject) => {
            // add authorisation header
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token

            axios.post('/logout')
                .then(response => {
                    commit('logout');

                    // remove loader
                    commit('removeFromLoadingQueue', 'user_logout', {root: true});

                    resolve(response);
                })
                .catch(error => {
                    commit('logout');

                    // remove loader
                    commit('removeFromLoadingQueue', 'user_logout', {root: true});

                    reject(error);
                });
        });
    },

    /**
     * load user info data
     * @param context
     * @returns {Promise<any>}
     */
    userInfo({ commit }) {
        return new Promise((resolve, reject) => {
            axios.get('/user/info')
                .then(response => {
                    commit('setUserInfo', response.data)
                    commit('isLoggedIn', true)
                    resolve(response);
                })
                .catch(error => {
                    if(error.response.status == 401)
                        commit('isLoggedIn', false)
                    else if(error.response.status == 403)
                        commit('isLoggedIn', false)

                    reject(error);
                });
        });
    },

    updateUserInfo({ commit, state }, userInfo) {
        // set loader
        commit('addToLoadingQueue', 'user_info_update', { root: true });

        // form method spoofing since file upload wont work with put
        // https://laravel.com/docs/5.3/routing#form-method-spoofing
        userInfo.append('_method', 'put')

        return new Promise((resolve, reject) => {
            // send update request
            axios.post('/user/info/' + state.userInfo.id , userInfo, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'multipart/form-data'
                }
            })
              .then((response) => {
                  // update user info
                  commit('setUserInfo', response.data);

                  // remove loader
                  commit('removeFromLoadingQueue', 'user_info_update', {root: true});

                  resolve(response);
              })
              .catch((error) => {
                  commit('removeFromLoadingQueue', 'user_info_update', {root: true});

                  commit('error/newError', {
                      title: 'error.user_info.update',
                      text: error.response? error.response.data.message || error.message: error.message
                  }, { root: true});

                  reject(error);
              })
        });
    },

    updatePassword({ commit, state }, password) {
        // set loader
        commit('addToLoadingQueue', 'password_update', { root: true });

        return new Promise((resolve, reject) => {
            // send update request
            axios.put('/user/password/' + state.userInfo.id , password)
              .then((response) => {
                  // remove loader
                  commit('removeFromLoadingQueue', 'password_update', {root: true});

                  resolve(response);
              })
              .catch((error) => {
                  commit('removeFromLoadingQueue', 'password_update', {root: true});

                  commit('error/newError', {
                      title: 'error.password.update',
                      text: error.response? error.response.data.message || error.message: error.message
                  }, { root: true});

                  reject(error);
              })
        });
    }
}

// mutations
const mutations = {
    isLoggedIn(state, status) {
        state.isLoggedIn = status
    },

    logout(state) {
        localStorage.removeItem('user_access_token');

        state.token = null;
        state.userInfo = null;
        state.data = null;
    },

    /**
     * set auth token
     * @param state
     * @param token
     */
    setToken(state, token) {
        state.token = token;
        localStorage.setItem('user_access_token', token);
    },

    /**
     * set user data
     * @param state
     * @param userInfo
     */
    setUserInfo(state, userInfo) {
        state.roles = userInfo.roles;
        delete userInfo.roles;
        state.userInfo = Object.assign({}, userInfo);
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
